import { dialog, ScrollablePanel } from '@Common/Controls';
import { extensions } from '@Common/Core/AppearanceExtension';
import constants from '@Common/Core/Constant';

import { properties } from '@Common/Core/Properties';
import { MessageBox } from '@Common/Dialogs/MessageBox';
import { ApplicationType, FilterType } from '@Common/Enums';
import errorHelper from '@Common/Helpers/ErrorHelper';
import pointerHelper from '@Common/Helpers/PointerHelper';
import urlHelper from '@Common/Helpers/UrlHelper';
import WindowResizeHelper from '@Common/Helpers/WindowResizeHelper';
import { ScrollUtils } from '@View/Core/Scroll';

export function injectScript(url: string, isAsync: boolean, callback: IHandler): void {
    const s = document.createElement('script');
    s.type = 'text/javascript';
    s.src = url;
    if (isAsync) {
        s.async = true;
    }
    if (callback) {
        s.onload = callback;
    }
    document.getElementsByTagName('head')[0].appendChild(s);
}

// eslint-disable-next-line complexity
export function log(message: unknown, error?: string, exceptionText?: string): void {
    let obj =
        error || exceptionText
            ? {
                  message: message,
                  error: error,
                  stackTrace: exceptionText
              }
            : message;

    if (typeof console !== 'undefined') {
        if (properties.browser.msie || properties.isPrint) {
            try {
                obj = objectToString(obj);
            } catch (ex) {
                console.log('Cannot write object to string');
            }
        }
        console.log(obj);
        if (error) {
            console.error(error);
        }
    }
}

export function lowFirstLetter(value: string): string {
    return value.charAt(0).toLowerCase() + value.slice(1);
}

export const objectToString = (obj: unknown, prepend = ''): string => {
    return _.cond([
        [_.isNil, _.constant('')],
        [_.overSome(_.isString, _.isBoolean, _.isNumber), _.toString],
        [_.isFunction, _.constant('[native function]')],
        [_.isEmpty, _.constant('')],
        [_.isArray, array => array.map(_.unary(objectToString)).join(', ')],
        [
            _.isObject,
            object =>
                _.keysIn(object)
                    .map((property, index) => {
                        const prependNewLine = index === 0 ? '\n' : '';
                        return `${prependNewLine + prepend + property}: ${objectToString(object[property], prepend + '    ')}`;
                    })
                    .concat('')
                    .join(';\n')
        ],
        [_.stubTrue, _.constant('')]
    ])(obj);
};

export function compareValues(value1, operator: FilterType, value2) {
    switch (operator) {
        case FilterType.equal:
            return value1 === value2;
        case FilterType.notEqual:
            return value1 !== value2;
        case FilterType.less:
            return value1 < value2;
        case FilterType.lessOrEqual:
            return value1 <= value2;
        case FilterType.greater:
            return value1 > value2;
        case FilterType.greaterOrEqual:
            return value1 >= value2;
    }

    return false;
}

export function getImageSrc(imageData: string | { $type?: string; $value: string } | undefined): string {
    if (_.isString(imageData)) {
        if (_.includes(imageData, '.')) {
            // Assuming that this is URL which points to image file
            return imageData;
        }
    } else {
        imageData = imageData?.$value;
    }

    const mimeType = 'image/png';
    if (imageData && imageData.length > 10240) {
        const byteString = atob(imageData);
        const byteArray = new Uint8Array(byteString.length);
        for (let i = 0; i < byteString.length; i++) {
            byteArray[i] = byteString.charCodeAt(i);
        }
        const blob = new Blob([byteArray], { type: mimeType });
        return `${URL.createObjectURL(blob)}`;
    }
    return `data:${mimeType};base64,${imageData}`;
}

export type IPopupControl = { readonly click: (fn: IHandler) => void; readonly close: IHandler };

export function createPopUp(param: any, control?: any): IPopupControl {
    let click = $.noop;

    const popup = $('<div class="popup"/>');
    control ? control.prepend(popup) : popup.appendTo($('body'));
    popup
        .on(pointerHelper.pointerDownEventName, function (e) {
            e.preventDefault(); // Disable click event

            if (param.hideOnClick) {
                popup.remove();
            }
            click();
        })
        .filter(function () {
            return param.opacity;
        })
        .addClass('backdrop')
        .end();

    if (param.zIndex) {
        popup.css('z-index', param.zIndex);
    }

    return {
        click: function (fn) {
            if (_.isFunction(fn)) {
                click = fn;
            }
        },
        close: function (isSilent = false) {
            if (!isSilent) {
                click();
            }

            popup.remove();
        }
    };
}

export function debug(echoObj: unknown): void {
    if (properties.isPrint) {
        objectToString(echoObj);
    }
    console.debug(echoObj);
}

export function executeScript(actionText: string, exceptionText: string, callback: IHandler): void {
    if (!callback) {
        return;
    }
    debug({ startExecuteScript: actionText });
    try {
        if (properties.isDebug) {
            callback();
        } else {
            safeExec(callback, exceptionText);
        }
    } finally {
        debug({ endExecuteScript: actionText });
    }
}

function safeExec(exec: IHandler, exceptionText = ''): void {
    try {
        exec();
    } catch (e) {
        log(exceptionText, e);
        errorHelper.showError(exceptionText, e);
    }
}

/**@deprecated  use _.clone or _.cloneDeep*/
export function clone<T>(source: T, references?): T {
    let newObject,
        existingReference,
        type = typeof source,
        isDOM = source instanceof HTMLElement;

    references = references || [];

    if (source === null) {
        return source;
    }

    if (type === 'object' || type === 'function') {
        let objectPair;
        for (let i = 0; i < references.length; i++) {
            objectPair = references[i];
            if (objectPair[0] === source) {
                existingReference = objectPair[1];
                break;
            }
        }
        if (existingReference) {
            return existingReference;
        } else {
            if (isDOM) {
                throw new Error('Tried to clone an DOM element');
                // newObject = source.cloneNode(true);
            } else if (type === 'object') {
                switch (Object.prototype.toString.call(source)) {
                    case '[object Date]':
                        newObject = new Date((source as any).valueOf());
                        break;
                    default:
                        newObject = new (source as any).constructor();
                        break;
                }
            } else {
                newObject = source;
            }

            references.push([source, newObject]);

            // if (!isDOM) {
            for (const key in source) {
                const propertyDescriptor = Object.getOwnPropertyDescriptor(source, key);
                if (propertyDescriptor) {
                    if (propertyDescriptor.get || propertyDescriptor.set) {
                        extensions.defineProperty(newObject, key, propertyDescriptor.get, propertyDescriptor.set);
                    } else {
                        newObject[key] = clone(source[key], references);
                    }
                }
            }
            // }
        }
    }
    return newObject || source;
}

export function visitObject(value, callback, objectCache = null, visitComplexObject = true): void {
    objectCache = objectCache || [];

    const stringValue = Object.prototype.toString.call(value);
    if (stringValue === '[object Array]') {
        if (objectCache.indexOf(value) === -1) {
            objectCache.push(value);
            _.forEach(value, element => {
                visitObject(element, callback, objectCache, visitComplexObject);
            });
        }
    } else if (stringValue === '[object Date]') {
        callback(value);
    } else if (typeof value === 'object') {
        visitComplexObject && callback(value);
        if (objectCache.indexOf(value) === -1) {
            objectCache.push(value);
            if (value != null) {
                for (const key of Object.keys(value)) {
                    visitObject(value[key], callback, objectCache, visitComplexObject);
                }
            }
        }
    } else {
        callback(value);
    }
}

const privateHTMLDiv = document.createElement('div');

export function htmlEncode(value: Nullable<string>): string {
    privateHTMLDiv.textContent = value ?? '';
    return privateHTMLDiv.innerHTML;
}

export function htmlDecode(value: string): string | null {
    privateHTMLDiv.innerHTML = value;
    return privateHTMLDiv.textContent;
}

export function getComponentTitle(title: Nullable<string>): string {
    if (title?.startsWith('<p')) {
        return title;
    }
    return `<p><span>${htmlEncode(title)}</span></p>`;
}

export function getDefaultName(
    prefix: string,
    existedNames: string[] | Dictionary<any>,
    formatter?: (prefix: string, index: number) => string
): string {
    let i = 0;
    let haveName = false;
    let name = '';
    while (!haveName) {
        name = formatter ? formatter(prefix, i) : i === 0 ? prefix : prefix + ' ' + i;
        i++;

        const lowerCaseName = name.toLowerCase();

        if (existedNames) {
            haveName = _.isArray(existedNames)
                ? !_.some(existedNames, (existedName: string) => existedName != null && lowerCaseName === existedName.toLowerCase())
                : existedNames[lowerCaseName] == null;
        } else {
            haveName = true;
        }
    }

    return name;
}

export function getIFrame(url): JQuery {
    return $('<iframe style="width:100%; height:100%;"/>').attr('src', urlHelper.completeUrl(url));
}

export function clearPopups(e?): void {
    if (e && e.isClearPopupsPrevented()) {
        return;
    }
    $('div.context-menu').remove();
    $('div.context-menu-progressbar').remove();
    $('div.border-highlight').trigger('removePopup');
    $('div.drop-down-content').trigger('removePopup');
    $('.menu-children.vertical').trigger('removePopup');
    $('.popup, .popup-control:not(.slide-popup)').trigger('removePopup');

    let skipSlidePopups = false;
    if ($('.popup-control:not(.slide-popup)').length) {
        skipSlidePopups = true;
    }
    if ($('#ui-datepicker-div:visible').length) {
        skipSlidePopups = true;
        if (e && $(e.target).parents('.ui-datepicker').length == 0) {
            $('.hasDatepicker').datepicker('hide');
        }
    }

    if (!skipSlidePopups) {
        $('.slide-popup').trigger('removePopup');
    }
}

// TODO: change to regular expression
export function getEmailsFromString(value: string): string[] {
    value = value.trim();
    if (value === '') {
        return [];
    }
    const separator = value.indexOf(';') !== -1 ? ';' : ',';
    return _.map(value.split(separator), v => {
        return v.replaceAll(' ', '');
    });
}

export function getStringFromEmails(emails: string[]): string {
    return emails.join(', ');
}

export function base64Encode(data: string): string {
    if (typeof btoa === 'function') {
        return btoa(data);
    }
    const b64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
    let o1,
        o2,
        o3,
        h1,
        h2,
        h3,
        h4,
        bits,
        i = 0,
        ac = 0,
        enc,
        tmpArr = [];

    if (!data) {
        return data;
    }

    do {
        o1 = data.charCodeAt(i++);
        o2 = data.charCodeAt(i++);
        o3 = data.charCodeAt(i++);

        bits = (o1 << 16) | (o2 << 8) | o3;

        h1 = (bits >> 18) & 0x3f;
        h2 = (bits >> 12) & 0x3f;
        h3 = (bits >> 6) & 0x3f;
        h4 = bits & 0x3f;

        tmpArr[ac++] = b64.charAt(h1) + b64.charAt(h2) + b64.charAt(h3) + b64.charAt(h4);
    } while (i < data.length);

    enc = tmpArr.join('');

    const r = data.length % 3;

    return (r ? enc.slice(0, r - 3) : enc) + '==='.slice(r || 3);
}

let lastUserActionTime = new Date();
export function initApp(applicationTypeId: ApplicationType, name?: string): void {
    const appTypeEnumValue = ApplicationType.getValue(applicationTypeId) || { name: applicationTypeId };
    altosoft.appId = applicationTypeId;
    altosoft.appName = name || appTypeEnumValue.name;

    errorHelper.attachErrorHandling();

    // Config 3rdparty libraries
    $.support.cors = true;
    $.ui.slider.prototype.numPages = 10;

    // Init our helpers
    ScrollUtils.extendIScroll();

    pointerHelper.setPointerEventsState(true);

    WindowResizeHelper.initialize();

    if (!window.__karma__) {
        $('body').attr('tabindex', -1).empty();
    }

    // Event handlers
    const jqWindow = $(window);

    window.oncontextmenu = function (e) {
        lastUserActionTime = new Date();
        return (e.target as HTMLElement).nodeName === 'INPUT' || (e.target as HTMLElement).nodeName === 'TEXTAREA';
    };

    $(document)
        .on('keydown', function (e) {
            lastUserActionTime = new Date();

            const code = e.keyCode ? e.keyCode : e.which;
            if (code === constants.keyCodes.BACKSPACE && e.target.nodeName !== 'INPUT' && e.target.nodeName !== 'TEXTAREA') {
                e.stopPropagation();
                e.preventDefault();
                return false;
            }

            if (code === constants.keyCodes.ESCAPE) {
                clearPopups(e);
            }
        })
        .on(pointerHelper.pointerDownEventName, function (e) {
            lastUserActionTime = new Date();

            clearPopups(e);
            jqWindow.triggerHandler(properties.lostFocusEventName, e.target);
        })
        .on(pointerHelper.pointerMoveEventName, function (e) {
            if (properties.isAndroid) {
                e.preventDefault();
            }
        });

    WindowResizeHelper.on(function () {
        lastUserActionTime = new Date();

        clearPopups();
    });

    startAutoLogon().shutdown();

    window.addEventListener(
        'focus',
        e => {
            const active = document.activeElement;
            if (
                !active ||
                (active.nodeName !== 'INPUT' &&
                    active.nodeName !== 'TEXTAREA' &&
                    active.nodeName !== 'SELECT' &&
                    (active.getAttribute('tabindex') == null || active.getAttribute('unselectable') === 'on'))
            ) {
                document.body.focus();
            }
        },
        true
    );
    window.addEventListener('blur', onElementBlurHandler, true);
}

export function onElementBlurHandler(): void {
    setTimeout(() => {
        const active = document.activeElement;
        if (!active || active.nodeName === 'BODY') {
            const nonModalDialog = document.querySelector('body>.dialog:not(.no-modal):last-of-type');
            if (nonModalDialog != null) {
                (nonModalDialog as HTMLElement).focus();
            }
        }
    }, 0);
}

export async function startAutoLogon(): Promise<void> {
    while ((altosoft.autoLogoutInterval ?? 0) > 0) {
        await Promise.wait(10000);
        const timeDiff = new Date().valueOf() - lastUserActionTime.valueOf();
        if (timeDiff > altosoft.autoLogoutInterval) {
            urlHelper.logout(true);
        }
    }
}

export function isCacheAllowed(): boolean {
    return altosoft.useCachedRequests && !properties.isPrint;
}

export function openHelpPage() {
    window.open(constants.knowledgeBaseUrl);
}

export function isSmallSize(width, height) {
    return Math.min(width, height) < constants.spinnerSize * 3;
}

export function getRealProjectId(projectId, projectName, defaultProjectid): string {
    let project = _.find(altosoft.model.user.projects, (item: any) => {
        return item.id === projectId;
    });
    if (!project) {
        project = _.find(altosoft.model.user.projects, (item: any) => {
            return item.name === projectName;
        });
    }

    return project ? project.id : defaultProjectid;
}

const promiseThenSrc = String($.Deferred().then);

export function isESPromise<T>(value): value is Promise<T> {
    return Promise.resolve(value) === value;
}

export function isPromise<T>(value): value is JQueryPromise<T> {
    if (value && _.isFunction(value.then)) {
        const valueThenSrc = String(value.then);
        return promiseThenSrc === valueThenSrc;
    }
    return false;
}

export function isPromiseLike<T>(value): value is PromiseLike<T> {
    return isESPromise(value) || isPromise(value);
}

export function isJQuery(value): value is JQuery {
    return value instanceof jQuery;
}

export function getFullDocumentPath(projectName, parentNames: string[]): string {
    const fullName = parentNames.join('\\');

    return projectName ? '{0}: {1}'.format(projectName, fullName) : fullName;
}

export function getSupportedLocales(): { id: string; name: string }[] {
    return _(altosoft.supportedLocales as _.Dictionary<string>)
        .map((value, key) => ({ id: key, name: value }))
        .sortBy(v => v.id)
        .value();
}

export function showImportResults(importResult: { messages: string[]; isFailed?: boolean }, callback?: () => void): Promise<void> {
    return new Promise(resolve => {
        const cb = () => {
            resolve();
            callback && callback();
        };

        if (importResult.messages.length === 0) {
            const messageBox = new MessageBox({
                dialogTitle: strings.importProject.resultTitle,
                text: strings.importProject.importSuccess
            });
            messageBox.onClosed(cb);
            messageBox.show();
        } else {
            const ul = $('<ul />').css({
                paddingLeft: '30px'
            });
            _.forEach(importResult.messages, message => {
                $('<li />').css({ 'list-style-type': 'disc' }).text(message).appendTo(ul);
            });
            const control = $('<div />').append(ul);

            const scrollContainer = new ScrollablePanel({ css: { padding: '5px' } });
            scrollContainer.append(control);

            if (importResult.isFailed) {
                errorHelper.showError(strings.importProject.importFailed, scrollContainer, null, cb);
            } else {
                const dlg = dialog({
                    title: strings.importProject.resultTitle,
                    width: 620,
                    height: 350
                });
                dlg.ok(cb);
                dlg.cancel(cb);
                dlg.wrap(scrollContainer.getControl());
            }
        }
    });
}

export function recursionLocker(): { lock: ICallback<IHandler> } {
    let running = false;

    function lock(fn: IHandler): void {
        if (!_.isFunction(fn)) {
            return;
        }
        if (running) {
            return;
        }
        running = true;
        fn();
        running = false;
    }

    return {
        lock: lock
    };
}
